import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { getQuestionById } from '../apis/question';
import ActiveChatPopup from '../components/ActiveChatPopup';
import { Input } from '../components/ChatInput/index.types';
import FullScreenError from '../components/Error/FullScreen';
import SpinnerLoader from '../components/Loader/Spinner';
import { resetTypewriterAnimationQueue } from '../components/Message/TypewriterText';
import { Message } from '../components/Message/index.types';
import { LOADING_TEXT, MESSAGE_TEXT } from '../constants/conversation';
import { Product } from '../enums/booking';
import { Language } from '../enums/common';
import { ActorType, SourceType, WidgetName, WidgetType } from '../enums/conversation';
import { Event } from '../enums/event';
import useNPS, { Config as NPSConfig, SubmitHandler } from '../hooks/useNPS';
import { useNativeFooter } from '../hooks/useNativeFooter';
import { writeLastSeq } from '../hooks/useSocketV3';
import { recordEvent, recordTaraEvent } from '../utils/analytics';
import { getUniformProduct } from '../utils/booking';
import { taraStatus } from '../utils/conversation';
import logger from '../utils/logger';
// Reuse the shared context so existing consumers (ChatFooter, MessageWrapper, etc.)
// pick up v3 state without any change.
import { ConversationContext } from './conversation';
import { Conversation, SendMessageFunction } from './index.types';

type Props = PropsWithChildren & {
    socket: Socket;
};

function ConversationProviderV3({ socket, children }: Props) {
    const isConversationLoaded = useRef(false);
    const timer = useRef<number>();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;
    const { pause, play } = useNativeFooter();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [conversation, setConversation] = useState<Conversation>();
    const [input, setInput] = useState<Input>();
    const [messages, setMessages] = useState<Message[]>([]);
    const [typing, setTyping] = useState(false);
    const [activeChatPopup, setActiveChatPopup] = useState<boolean>(false);
    const [lastMessage, setLastMessage] = useState<string>('');
    const [showReconnectionBanner, setShowReconnectionBanner] = useState({ onOffline: false, onConnectionLost: false });
    const [reconnecting, setReconnecting] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>();
    const [initialParams] = useState<Record<string, string>>({
        botId: searchParams.get('botId') || '',
        flowId: searchParams.get('flowId') || '',
        sourceType: searchParams.get('sourceType') || '',
        sourceValue: searchParams.get('sourceValue') || '',
        lang: searchParams.get('lang') || Language.EN,
        client: searchParams.get('client') || '',
    });

    const id = searchParams.get('id');
    const bookingId = searchParams.get('bookingId');
    const product = searchParams.get('product');

    useEffect(() => {
        timer.current = Date.now();
    }, []);

    useEffect(() => {
        if (!loading && timer.current) {
            logger({
                id: 'TARA-V3-LOADING-COMPLETE',
                type: 'info',
                data: {
                    ...initialParams,
                    id,
                    bookingId,
                    product: product?.toLowerCase(),
                    timeTaken: Date.now() - (timer.current || 0),
                },
            });
            timer.current = undefined;
        }
    }, [loading, initialParams]);

    const processMessages = (items: Message[], isLiveSession: boolean) =>
        items.map((item) => ({
            ...item,
            isLiveSession,
            messageParts: item.messageParts.map((part) => ({ ...part, id: part.id || uuidv4() })),
        }));

    const setMessagesWithId = (data: Message[], options?: { replace?: boolean; isLiveSession?: boolean }) => {
        const isLiveSession = options?.replace ? false : (options?.isLiveSession ?? true);
        setMessages((prev) => {
            const processed = processMessages(data, isLiveSession);
            if (options?.replace) {
                resetTypewriterAnimationQueue();
                return processed;
            }
            const newMessages = [...prev];
            for (const item of processed) {
                const at = newMessages.findIndex((m) => m._id === item._id);
                if (at === -1) {
                    newMessages.push(item);
                } else {
                    const existing = newMessages[at];
                    const mergedParts = item.messageParts.map((nextPart) => {
                        const prevPart = existing.messageParts.find((p) => p.id === nextPart.id);
                        if (prevPart?.file?.localUrl && nextPart.file && !nextPart.file.localUrl) {
                            return { ...nextPart, file: { ...nextPart.file, localUrl: prevPart.file.localUrl } };
                        }
                        return nextPart;
                    });
                    newMessages[at] = {
                        ...item,
                        isLiveSession: existing.isLiveSession || item.isLiveSession,
                        messageParts: mergedParts,
                    };
                }
            }
            return newMessages;
        });
    };

    const sendMessage: SendMessageFunction = (reply) => {
        const isModeUndefined = reply.mode === undefined;
        if (taraStatus.isEnabled() && isModeUndefined) reply.mode = 'SPEECH';

        if (input?.events) {
            input.events.forEach((event) => {
                if (event.recordValue) event.parameters.value = reply.messageParts[0].text?.content || '';
                recordTaraEvent(event.name, event.parameters);
            });
        }

        socket.emit('reply', reply);
        pause();
        setInput(undefined);

        if (reply.messageParts.some((part) => !part.isPrivate)) {
            setMessagesWithId([
                {
                    _id: uuidv4(),
                    actor: { type: ActorType.USER },
                    conversationId: conversation?._id || '',
                    createdAt: Date.now(),
                    isSeen: true,
                    messageParts: reply.messageParts,
                },
            ]);
            if (!conversation?.isAgentActive) setTyping(true);
        }

        if (!isModeUndefined && !conversation?.isAgentActive) {
            const uniformProduct = getUniformProduct(product || '');
            recordEvent(Event.TARA_INPUT, {
                additionalParams: {
                    conversationId: id,
                    lang: initialParams.lang,
                    mode: reply.mode!,
                    product: uniformProduct,
                    tripId: bookingId,
                },
            });
        }
    };

    const onNPSSubmit: SubmitHandler = (value, metaData, config) => {
        const content = `<b>Rating:</b> ${value} ${Array(value).fill('&#11088;').join(' ')}
        <b>Agent:</b> ${metaData.agent || 'Unknown'}
        ${metaData.email ? `<b>Email:</b> ${metaData.email}` : ''}`;

        const messageParts = [{ isPrivate: true, text: { content } }];

        if (value === 1 && !config?.goBackOnSubmit?.includes(1)) {
            messageParts.push({
                isPrivate: false,
                text: { content: 'No, I am unhappy with my query resolution.' },
            });
        }

        sendMessage({ messageParts, input: { value, metaData } });
    };

    const { loadNPS } = useNPS(onNPSSubmit);

    const initConversation = () => {
        const { botId, flowId, sourceType, sourceValue, lang } = initialParams;
        switch (sourceType) {
            case SourceType.QUESTION: {
                getQuestionById(sourceValue || '').then(
                    (data) => {
                        const header = data?.header[lang as Language] || data?.header[Language.EN];
                        const contentId = searchParams.get('contentId');
                        let content = data?.contents.filter((item) => item._id === contentId)[0];
                        if (!content) content = data?.contents[0];
                        const dialogueFlowId = content?.contacts.chat.tag?.replace('dialogueFlow_', '');
                        socket.emit('create', {
                            bookingId,
                            dialogueFlowId,
                            messageParts: [{ text: { content: header } }],
                            mode: taraStatus.isEnabled() ? 'SPEECH' : undefined,
                        });
                    },
                    () => setError(true),
                );
                break;
            }
            case SourceType.TICKET: {
                socket.emit('create', {
                    bookingId,
                    dialogueFlowId: '65f2d4ccc16f290012e78e83',
                    messageParts: [
                        {
                            text: {
                                content: MESSAGE_TEXT[SourceType.TICKET].replace('{{ticketId}}', sourceValue || ''),
                            },
                        },
                    ],
                    mode: taraStatus.isEnabled() ? 'SPEECH' : undefined,
                });
                break;
            }
            case SourceType.WIDGET: {
                let content = '';
                switch (sourceValue) {
                    case WidgetType.REFUND_TIMELINE: {
                        content = MESSAGE_TEXT[WidgetType.REFUND_TIMELINE];
                        break;
                    }
                    default:
                        break;
                }
                socket.emit('create', {
                    bookingId,
                    dialogueFlowId: product === Product.TRAIN ? '664af9c38ae14f0012333391' : '65eec0e30adf68001231cc6a',
                    messageParts: [{ text: { content } }],
                    mode: taraStatus.isEnabled() ? 'SPEECH' : undefined,
                });
                break;
            }
            default:
                socket.emit('create', { bookingId, botId, dialogueFlowId: flowId });
        }
    };

    const onConversationCreated = (data: Conversation) => {
        if (isConversationLoaded.current) return;
        isConversationLoaded.current = true;
        setSearchParams(
            `id=${data?._id}&bookingId=${bookingId}&product=${product}&lang=${initialParams.lang}${initialParams.client ? `&client=${initialParams.client}` : ''}`,
            { replace: true },
        );
        setConversation({ ...data, messages: [] });
        setMessagesWithId(data.messages ?? [], { replace: true });
        setLoading(false);
    };

    const onConversationStarted = (data: Conversation) => {
        const conversationId = searchParamsRef.current.get('id');
        if (!data?.isActive) {
            initConversation();
            return;
        }
        onConversationCreated(data);
        if (conversationId) return;
        const lastMsg = data.messages[data.messages.length - 1];
        const lastMsgContent = lastMsg?.messageParts[0]?.text?.content;
        if (lastMsg?.actor.type === ActorType.BOT && lastMsgContent) {
            setLastMessage(lastMsgContent);
            setActiveChatPopup(true);
            return;
        }
        initConversation();
    };

    useEffect(() => {
        resetTypewriterAnimationQueue();
    }, []);

    useEffect(() => {
        socket.on('conversation_created', onConversationCreated);
        socket.on('conversation_started', onConversationStarted);
        socket.on('conversation_updated', (data) => setConversation({ ...data, messages: [] }));
        socket.on('input_request', (data: Input) => setInput(data));

        socket.on('message', (data: Message, callback) => {
            // v3: ACK so server can stamp deliveredAt and skip this on next replay.
            if (callback) callback({ received: true });
            socket.emit('ack', { messageId: data._id, conversationId: data.conversationId });

            // Track highest seq we've seen for this conversation — sent in auth on reconnect.
            if (typeof data.seq === 'number' && data.conversationId) {
                writeLastSeq(data.conversationId, data.seq);
            }

            if (data.events) {
                data.events.forEach((event) => recordTaraEvent(event.name, event.parameters));
            }

            setMessagesWithId([data]);
            if (data.actor.type !== ActorType.USER) setTyping(false);

            const npsRequest = data.messageParts.filter((item) => item.widget?.name === WidgetName.NPS_COLLECTION)[0];
            if (npsRequest)
                loadNPS(npsRequest.widget?.parameters.url as string, npsRequest.widget?.parameters.config as NPSConfig);
        });

        socket.on('message_video', (data: { url: string }) => {
            const isEnabled = taraStatus.isEnabled();
            if (isEnabled) play(data.url);
            logger({ id: 'TARA-V3-SOCKET-EVENT', type: 'info', eventName: 'message_video', data, isEnabled });
        });

        socket.on('show_reconnection_banner', (data: { onOffline: boolean; onConnectionLost: boolean }) =>
            setShowReconnectionBanner(data),
        );

        socket.on('disconnect', () => showReconnectionBanner.onConnectionLost && setReconnecting(true));
        socket.on('connect', () => setReconnecting(false));

        socket.emit('load', id ? { conversationId: id } : { metaData: { id: bookingId } });
    }, []);

    useEffect(() => {
        const handleOffline = () => showReconnectionBanner.onOffline && setReconnecting(true);
        const handleOnline = () => setReconnecting(false);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [showReconnectionBanner.onOffline]);

    useEffect(() => {
        if (!activeChatPopup) setLastMessage('');
    }, [activeChatPopup]);

    const value = useMemo(
        () => ({
            conversation,
            input,
            messages,
            sendMessage,
            typing,
            reconnecting,
            selectedBookingId,
            setSelectedBookingId,
        }),
        [conversation, input, messages, typing, reconnecting, selectedBookingId],
    );

    if (error) return <FullScreenError />;

    if (loading) {
        return (
            <div className='flex justify-center items-center h-full'>
                <SpinnerLoader text={LOADING_TEXT} />
            </div>
        );
    }

    return (
        <ConversationContext.Provider value={value}>
            {children}
            <ActiveChatPopup
                lastMessage={lastMessage}
                open={activeChatPopup}
                onClose={() => setActiveChatPopup(false)}
                onResolve={() => {
                    setInput(undefined);
                    setActiveChatPopup(false);
                    setTyping(true);
                    initConversation();
                }}
                onView={() => setActiveChatPopup(false)}
            />
        </ConversationContext.Provider>
    );
}

export default ConversationProviderV3;
